import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Grid, Tab, Tabs, TextField, Typography, Chip, MenuItem,
} from '@mui/material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { PageHeader } from '../../components/ui/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { fleetReportAPI, vehicleAPI } from '../../services/api';
import { brandColors, RADIUS } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { useSystemTotal } from '../../hooks/useSystemTotal';

const monthDefault = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
};

const money = (n: number) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export const FleetReportsPage: React.FC = () => {
  const { total: systemTotal } = useSystemTotal('fleet_reports');
  const [tab, setTab] = useState(0);
  const [month, setMonth] = useState(monthDefault());
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [vehicleOptions, setVehicleOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [vehicleRows, setVehicleRows] = useState<Record<string, unknown>[]>([]);
  const [tripRows, setTripRows] = useState<Record<string, unknown>[]>([]);
  const [tripByStatus, setTripByStatus] = useState<Record<string, number>>({});
  const [summary, setSummary] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    vehicleAPI.getAll().then((res) => {
      const rows = (res.data || []) as { vehicleNumber?: string }[];
      const plates = Array.from(new Set(
        rows
          .map((v) => String(v.vehicleNumber || '').toUpperCase())
          .filter((n): n is string => Boolean(n))
      )).sort();
      setVehicleOptions(plates);
    }).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { month, ...(vehicleNumber.trim() ? { vehicleNumber: vehicleNumber.trim() } : {}) };
      const [veh, trips, accounts] = await Promise.all([
        fleetReportAPI.monthlyVehicles(params),
        fleetReportAPI.monthlyTrips(params),
        fleetReportAPI.monthlyAccounts({ month }),
      ]);
      setVehicleRows((veh.data.rows || []).map((r: Record<string, unknown>, i: number) => ({ ...r, _id: String(i) })));
      setTripRows((trips.data.rows || []).map((r: Record<string, unknown>, i: number) => ({ ...r, _id: String(i) })));
      setTripByStatus(trips.data.byStatus || {});
      setSummary(accounts.data);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to load month reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accounts = summary as {
    cashMemo?: { credit: number; debit: number; count: number };
    fastag?: { debit: number; credit: number; tripCount: number };
    battery?: { total: number; count: number };
  } | null;

  return (
    <Box>
      <PageHeader
        title="Month-wise Fleet Reports"
        subtitle={getPageSubtitle('fleet_reports', 'Fleet Reports', { total: systemTotal })}
        icon={<SummarizeIcon />}
      />

      <Card sx={{ mb: 2, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
        <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Month"
            type="month"
            size="small"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...catchyTextFieldSx, minWidth: 180 }}
          />
          <TextField
            select
            label="Licence Plate No."
            size="small"
            value={vehicleNumber}
            onChange={(e) => setVehicleNumber(e.target.value)}
            sx={{ ...catchyTextFieldSx, minWidth: 220 }}
          >
            <MenuItem value="">All vehicles</MenuItem>
            {vehicleOptions.map((plate) => (
              <MenuItem key={plate} value={plate}>{plate}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={load} disabled={loading} sx={catchyPrimaryButtonSx}>
            Run report
          </Button>
          {error && <Typography color="error" fontSize={13}>{error}</Typography>}
        </CardContent>
      </Card>

      {accounts && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: 'Cash Book Debit', value: money(accounts.cashMemo?.debit || 0) },
            { label: 'Cash Book Credit', value: money(accounts.cashMemo?.credit || 0) },
            { label: 'FASTag Debit', value: money(accounts.fastag?.debit || 0) },
            { label: 'Battery Maint.', value: money(accounts.battery?.total || 0) },
          ].map((card) => (
            <Grid item xs={6} sm={3} key={card.label}>
              <Box sx={{ p: 1.5, bgcolor: '#fff', border: `1px solid ${brandColors.border}`, borderRadius: 2 }}>
                <Typography fontSize={11} color="text.secondary" fontWeight={700}>{card.label}</Typography>
                <Typography fontSize={16} fontWeight={800} mt={0.5}>{card.value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label={`By Vehicle (${vehicleRows.length})`} />
        <Tab label={`By Trip (${tripRows.length})`} />
      </Tabs>

      {tab === 0 && (
        <DataTable
          columns={[
            { field: 'vehicleNumber', header: 'Licence Plate No.' },
            { field: 'tripCount', header: 'Trips' },
            { field: 'fuelAmount', header: 'Fuel', format: 'currency' },
            { field: 'maintenanceAmount', header: 'Service', format: 'currency' },
            { field: 'batteryAmount', header: 'Battery', format: 'currency' },
            { field: 'fastagDebit', header: 'FASTag Debit (Rs.)', format: 'currency' },
            { field: 'fastagTripCount', header: 'Tag Trip Count' },
            { field: 'cashDebit', header: 'Cash Debit (Rs.)', format: 'currency' },
          ]}
          rows={vehicleRows}
          loading={loading}
          search=""
          onSearchChange={() => {}}
          page={0}
          rowsPerPage={25}
          onPageChange={() => {}}
          onRowsPerPageChange={() => {}}
          selectable={false}
          recordLabel="vehicles"
          emptyMessage="No vehicle data for this month"
        />
      )}

      {tab === 1 && (
        <>
          <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(tripByStatus).map(([k, v]) => (
              <Chip key={k} size="small" label={`${k}: ${v}`} />
            ))}
          </Box>
          <DataTable
            columns={[
              { field: 'tripNumber', header: 'Trip #' },
              { field: 'startDate', header: 'Date', format: 'date' },
              { field: 'vehicleNumber', header: 'Licence Plate No.' },
              { field: 'driverName', header: 'Driver' },
              { field: 'clientName', header: 'Client' },
              { field: 'source', header: 'Source' },
              { field: 'destination', header: 'Destination' },
              { field: 'status', header: 'Status' },
            ]}
            rows={tripRows}
            loading={loading}
            search=""
            onSearchChange={() => {}}
            page={0}
            rowsPerPage={25}
            onPageChange={() => {}}
            onRowsPerPageChange={() => {}}
            selectable={false}
            recordLabel="trips"
            emptyMessage="No trips for this month"
          />
        </>
      )}
    </Box>
  );
};
